import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  Grid,
  MenuItem,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Checkbox,
  Menu,
} from '@mui/material';
import {
  Call as CallIcon,
  Sms as SmsIcon,
  Message as MessageIcon,
  OpenInNew as OpenIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  MoneyOff as MoneyOffIcon,
  Security as SecurityIcon,
  FileDownload as ExportIcon,
} from '@mui/icons-material';
import { adminAPI } from '../services/api';
import { format } from 'date-fns';

const Activity = () => {
  const navigate = useNavigate();
  
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  
  // Filters — persist to sessionStorage so they survive tab navigation
  const [eventType, setEventType] = useState(() => sessionStorage.getItem('activity_event_type') || '');
  const [riskLevel, setRiskLevel] = useState(() => sessionStorage.getItem('activity_risk_level') || '');
  const [dateRange, setDateRange] = useState(() => sessionStorage.getItem('activity_date_range') || '24h');

  // Selection & export
  const [selected, setSelected] = useState(new Set());
  const [exportAnchor, setExportAnchor] = useState(null);

  // Detail dialog
  const [detailDialog, setDetailDialog] = useState(null);
  const [moneySentLoading, setMoneySentLoading] = useState(false);
  const [piiDisclosedLoading, setPiiDisclosedLoading] = useState(false);

  // Persist filters to sessionStorage on change
  useEffect(() => {
    sessionStorage.setItem('activity_event_type', eventType);
    sessionStorage.setItem('activity_risk_level', riskLevel);
    sessionStorage.setItem('activity_date_range', dateRange);
  }, [eventType, riskLevel, dateRange]);

  useEffect(() => {
    loadActivity();
  }, [page, rowsPerPage, eventType, riskLevel, dateRange]);

  const loadActivity = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getGlobalActivity({
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        event_type: eventType || undefined,
        risk_level: riskLevel || undefined,
        date_range: dateRange || undefined,
      });
      setActivity(response.data.activity || []);
      setTotal(response.data.total || 0);
      setSelected(new Set());
    } catch (err) {
      console.error('Failed to load activity:', err);
      setError('Failed to load activity feed');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleMoneySent = async () => {
    if (!detailDialog) return;
    const newValue = !detailDialog.money_sent;
    setMoneySentLoading(true);
    try {
      await adminAPI.updateEventStatus(detailDialog.id, { money_sent: newValue });
      const updates = {
        money_sent: newValue,
        money_sent_marked_at: newValue ? new Date().toISOString() : null,
        ...(newValue ? {} : { money_sent_acknowledged: false, money_sent_acknowledged_at: null }),
      };
      setDetailDialog(prev => ({ ...prev, ...updates }));
      setActivity(prev => prev.map(e => e.id === detailDialog.id ? { ...e, ...updates } : e));
    } catch (err) {
      console.error('Failed to toggle money_sent:', err);
      setError('Failed to update money sent status');
    } finally {
      setMoneySentLoading(false);
    }
  };

  const handleAcknowledgeMoneySent = async () => {
    if (!detailDialog) return;
    setMoneySentLoading(true);
    try {
      await adminAPI.acknowledgeMoneySent(detailDialog.id);
      const updates = {
        money_sent_acknowledged: true,
        money_sent_acknowledged_at: new Date().toISOString(),
      };
      setDetailDialog(prev => ({ ...prev, ...updates }));
      setActivity(prev => prev.map(e => e.id === detailDialog.id ? { ...e, ...updates } : e));
    } catch (err) {
      console.error('Failed to acknowledge money_sent:', err);
      setError('Failed to acknowledge money sent');
    } finally {
      setMoneySentLoading(false);
    }
  };

  const handleTogglePiiDisclosed = async () => {
    if (!detailDialog) return;
    const newValue = !detailDialog.pii_disclosed;
    setPiiDisclosedLoading(true);
    try {
      await adminAPI.updateEventStatus(detailDialog.id, { pii_disclosed: newValue });
      const updates = {
        pii_disclosed: newValue,
        pii_disclosed_marked_at: newValue ? new Date().toISOString() : null,
        ...(newValue ? {} : { pii_disclosed_acknowledged: false, pii_disclosed_acknowledged_at: null }),
      };
      setDetailDialog(prev => ({ ...prev, ...updates }));
      setActivity(prev => prev.map(e => e.id === detailDialog.id ? { ...e, ...updates } : e));
    } catch (err) {
      console.error('Failed to toggle pii_disclosed:', err);
      setError('Failed to update PII disclosed status');
    } finally {
      setPiiDisclosedLoading(false);
    }
  };

  const handleAcknowledgePiiDisclosed = async () => {
    if (!detailDialog) return;
    setPiiDisclosedLoading(true);
    try {
      await adminAPI.acknowledgePiiDisclosed(detailDialog.id);
      const updates = {
        pii_disclosed_acknowledged: true,
        pii_disclosed_acknowledged_at: new Date().toISOString(),
      };
      setDetailDialog(prev => ({ ...prev, ...updates }));
      setActivity(prev => prev.map(e => e.id === detailDialog.id ? { ...e, ...updates } : e));
    } catch (err) {
      console.error('Failed to acknowledge pii_disclosed:', err);
      setError('Failed to acknowledge PII disclosed');
    } finally {
      setPiiDisclosedLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getEventIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'call':
      case 'voice':
      case 'inbound_call':
      case 'outbound_call':
        return <CallIcon fontSize="small" color="primary" />;
      case 'sms':
      case 'text':
      case 'inbound_sms':
        return <SmsIcon fontSize="small" color="success" />;
      default:
        return <MessageIcon fontSize="small" color="action" />;
    }
  };

  const getRiskColor = (riskLevel) => {
    switch (riskLevel?.toLowerCase()) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'success';
      default:
        return 'default';
    }
  };

  const formatPhone = (phone) => {
    if (!phone) return '-';
    return `***-***-${phone.slice(-4)}`;
  };

  // Selection helpers
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelected(new Set(activity.map(a => a.id)));
    } else {
      setSelected(new Set());
    }
  };

  const handleSelectOne = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const isAllSelected = activity.length > 0 && selected.size === activity.length;
  const isSomeSelected = selected.size > 0 && selected.size < activity.length;

  // Export helpers
  const getSelectedEntries = () => {
    return activity
      .filter(e => selected.has(e.id))
      .map(e => ({
        id: e.id,
        timestamp: e.created_at,
        channel: e.event_type || null,
        direction: e.call_direction || e.direction || null,
        from: e.from_phone || e.from_email || null,
        to: e.to_phone || e.to_email || null,
        raw_content: e.content || e.transcription || null,
        extracted_signals: e.extracted_signals || null,
        concern_level: e.risk_level || null,
        system_response: e.summary || null,
        metadata: e.metadata || null,
      }));
  };

  const downloadFile = (content, filename, mimeType) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const exportJSON = () => {
    const entries = getSelectedEntries();
    const ts = format(new Date(), 'yyyyMMdd_HHmmss');
    downloadFile(JSON.stringify(entries, null, 2), `secondclear_activity_export_${ts}.json`, 'application/json');
    setExportAnchor(null);
  };

  const exportCSV = () => {
    const entries = getSelectedEntries();
    const headers = ['id', 'timestamp', 'channel', 'direction', 'from', 'to', 'raw_content', 'extracted_signals', 'concern_level', 'system_response', 'metadata'];
    const escapeCSV = (val) => {
      if (val === null || val === undefined) return '';
      const str = typeof val === 'object' ? JSON.stringify(val) : String(val);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };
    const rows = [headers.join(',')];
    for (const entry of entries) {
      rows.push(headers.map(h => escapeCSV(entry[h])).join(','));
    }
    const ts = format(new Date(), 'yyyyMMdd_HHmmss');
    downloadFile(rows.join('\n'), `secondclear_activity_export_${ts}.csv`, 'text/csv');
    setExportAnchor(null);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            Activity Feed
          </Typography>
          <Typography variant="body1" color="text.secondary">
            All communications across the system
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {selected.size > 0 && (
            <>
              <Button
                variant="contained"
                size="small"
                startIcon={<ExportIcon />}
                onClick={(e) => setExportAnchor(e.currentTarget)}
              >
                Export ({selected.size})
              </Button>
              <Menu
                anchorEl={exportAnchor}
                open={Boolean(exportAnchor)}
                onClose={() => setExportAnchor(null)}
              >
                <MenuItem onClick={exportJSON}>Download as JSON</MenuItem>
                <MenuItem onClick={exportCSV}>Download as CSV</MenuItem>
              </Menu>
            </>
          )}
          <Tooltip title="Refresh">
            <span>
              <IconButton onClick={loadActivity} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              fullWidth
              label="Time Range"
              value={dateRange}
              onChange={(e) => { setDateRange(e.target.value); setPage(0); }}
              size="small"
            >
              <MenuItem value="1h">Last Hour</MenuItem>
              <MenuItem value="24h">Last 24 Hours</MenuItem>
              <MenuItem value="7d">Last 7 Days</MenuItem>
              <MenuItem value="30d">Last 30 Days</MenuItem>
              <MenuItem value="all">All Time</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              fullWidth
              label="Event Type"
              value={eventType}
              onChange={(e) => { setEventType(e.target.value); setPage(0); }}
              size="small"
            >
              <MenuItem value="">All Types</MenuItem>
              <MenuItem value="voice">Calls</MenuItem>
              <MenuItem value="sms">SMS</MenuItem>
              <MenuItem value="email">Email</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              fullWidth
              label="Risk Level"
              value={riskLevel}
              onChange={(e) => { setRiskLevel(e.target.value); setPage(0); }}
              size="small"
            >
              <MenuItem value="">All Levels</MenuItem>
              <MenuItem value="high">High Risk</MenuItem>
              <MenuItem value="medium">Medium Risk</MenuItem>
              <MenuItem value="low">Low Risk</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Showing {activity.length} of {total} events
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Activity Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={isSomeSelected}
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                    size="small"
                  />
                </TableCell>
                <TableCell>Type</TableCell>
                <TableCell>User</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>From Phone</TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Content</TableCell>
                <TableCell>Risk</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : activity.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No activity found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                activity.map((event) => (
                  <TableRow
                    key={event.id}
                    hover
                    selected={selected.has(event.id)}
                    sx={{
                      cursor: 'pointer',
                      bgcolor: event.money_sent ? '#fef2f2' : event.pii_disclosed ? '#fff7ed' : 'inherit',
                      borderLeft: event.money_sent ? '4px solid #dc2626' : event.pii_disclosed ? '4px solid #ea580c' : 'none',
                      '&:hover': { bgcolor: event.money_sent ? '#fee2e2' : event.pii_disclosed ? '#ffedd5' : 'action.hover' },
                    }}
                    onClick={() => setDetailDialog(event)}
                  >
                    <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        size="small"
                        checked={selected.has(event.id)}
                        onChange={() => handleSelectOne(event.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getEventIcon(event.event_type)}
                        <Typography variant="body2">
                          {event.event_type || 'message'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                        onClick={(e) => { e.stopPropagation(); navigate(`/users/${event.user_id}`); }}
                      >
                        {event.user_name || event.user_email || 'Unknown'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{formatPhone(event.from_phone)}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      <Tooltip title={event.content || event.transcription || event.summary || ''}>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: { xs: 150, sm: 200, md: 250 },
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {event.content || event.transcription || event.summary || '-'}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                        {event.money_sent && (
                          <Chip
                            icon={<MoneyOffIcon />}
                            label="Money Sent"
                            size="small"
                            color="error"
                          />
                        )}
                        {event.pii_disclosed && (
                          <Chip
                            icon={<SecurityIcon />}
                            label="PII Shared"
                            size="small"
                            sx={{ bgcolor: '#fff7ed', color: '#ea580c', borderColor: '#ea580c', border: '1px solid' }}
                          />
                        )}
                        {event.risk_level ? (
                          <Chip
                            label={event.risk_level}
                            size="small"
                            color={getRiskColor(event.risk_level)}
                          />
                        ) : (
                          !event.money_sent && !event.pii_disclosed && '-'
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {event.created_at ? format(new Date(event.created_at), 'MMM d, HH:mm') : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      <Tooltip title="View user">
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/users/${event.user_id}`); }}>
                          <OpenIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[25, 50, 100]}
        />
      </Paper>

      {/* Event Detail Dialog */}
      <Dialog open={!!detailDialog} onClose={() => setDetailDialog(null)} maxWidth="md" fullWidth PaperProps={{ sx: { overflow: 'hidden' } }}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {detailDialog && getEventIcon(detailDialog.event_type)}
              Event Details
            </Box>
            <IconButton size="small" onClick={() => setDetailDialog(null)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ overflowX: 'hidden' }}>
          {detailDialog && (
            <Grid container spacing={2} sx={{ overflow: 'hidden' }}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="text.secondary">Event Type</Typography>
                <Typography>{detailDialog.event_type || '-'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="text.secondary">Date</Typography>
                <Typography>{detailDialog.created_at ? format(new Date(detailDialog.created_at), 'PPpp') : '-'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="text.secondary">User</Typography>
                <Typography
                  sx={{ cursor: 'pointer', color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}
                  onClick={() => { setDetailDialog(null); navigate(`/users/${detailDialog.user_id}`); }}
                >
                  {detailDialog.user_name || detailDialog.user_email || detailDialog.user_id}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="text.secondary">From Phone</Typography>
                <Typography>{detailDialog.from_phone || '-'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="text.secondary">Risk Level</Typography>
                {detailDialog.risk_level ? (
                  <Chip label={detailDialog.risk_level} size="small" color={getRiskColor(detailDialog.risk_level)} />
                ) : (
                  <Typography>-</Typography>
                )}
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="text.secondary">Risk Score</Typography>
                <Typography>{detailDialog.risk_score ?? '-'}</Typography>
              </Grid>
              {detailDialog.call_direction && (
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">Call Direction</Typography>
                  <Typography sx={{ textTransform: 'capitalize' }}>{detailDialog.call_direction}</Typography>
                </Grid>
              )}
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {detailDialog.money_sent && (
                    <Chip icon={<MoneyOffIcon />} label="Money Sent" size="small" color="error" />
                  )}
                  {detailDialog.pii_disclosed && (
                    <Chip icon={<SecurityIcon />} label="PII Shared" size="small" sx={{ bgcolor: '#fff7ed', color: '#ea580c', borderColor: '#ea580c', border: '1px solid' }} />
                  )}
                  {detailDialog.is_flagged && <Chip label="Flagged" size="small" color="warning" />}
                  {detailDialog.is_read ? (
                    <Chip label="Read" size="small" variant="outlined" />
                  ) : (
                    <Chip label="Unread" size="small" color="info" />
                  )}
                </Box>
              </Grid>
              {/* Money Sent Controls */}
              <Grid item xs={12}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    bgcolor: detailDialog.money_sent ? '#fef2f2' : '#f9fafb',
                    borderColor: detailDialog.money_sent ? '#fca5a5' : undefined,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <MoneyOffIcon color={detailDialog.money_sent ? 'error' : 'disabled'} />
                      <Typography variant="subtitle2">
                        {detailDialog.money_sent ? 'Marked as Money Sent' : 'Money Sent Status'}
                      </Typography>
                      {detailDialog.money_sent && detailDialog.money_sent_acknowledged && (
                        <Chip label="Acknowledged" size="small" color="success" variant="outlined" />
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {detailDialog.money_sent && !detailDialog.money_sent_acknowledged && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="success"
                          disabled={moneySentLoading}
                          onClick={handleAcknowledgeMoneySent}
                        >
                          Acknowledge
                        </Button>
                      )}
                      <Button
                        size="small"
                        variant={detailDialog.money_sent ? 'outlined' : 'contained'}
                        color={detailDialog.money_sent ? 'inherit' : 'error'}
                        disabled={moneySentLoading}
                        onClick={handleToggleMoneySent}
                        startIcon={<MoneyOffIcon />}
                      >
                        {detailDialog.money_sent ? 'Unmark Money Sent' : 'Mark as Money Sent'}
                      </Button>
                    </Box>
                  </Box>
                  {detailDialog.money_sent && detailDialog.money_sent_marked_at && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Marked by {detailDialog.money_sent_marked_by || 'unknown'} on{' '}
                      {format(new Date(detailDialog.money_sent_marked_at), 'PPpp')}
                    </Typography>
                  )}
                </Paper>
              </Grid>

              {/* PII Disclosed Controls */}
              <Grid item xs={12}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    bgcolor: detailDialog.pii_disclosed ? '#fff7ed' : '#f9fafb',
                    borderColor: detailDialog.pii_disclosed ? '#fdba74' : undefined,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SecurityIcon sx={{ color: detailDialog.pii_disclosed ? '#ea580c' : 'action.disabled' }} />
                      <Typography variant="subtitle2">
                        {detailDialog.pii_disclosed ? 'Marked as PII Disclosed' : 'PII Disclosed Status'}
                      </Typography>
                      {detailDialog.pii_disclosed && detailDialog.pii_disclosed_acknowledged && (
                        <Chip label="Acknowledged" size="small" color="success" variant="outlined" />
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {detailDialog.pii_disclosed && !detailDialog.pii_disclosed_acknowledged && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="success"
                          disabled={piiDisclosedLoading}
                          onClick={handleAcknowledgePiiDisclosed}
                        >
                          Acknowledge
                        </Button>
                      )}
                      <Button
                        size="small"
                        variant={detailDialog.pii_disclosed ? 'outlined' : 'contained'}
                        sx={detailDialog.pii_disclosed ? {} : { bgcolor: '#ea580c', '&:hover': { bgcolor: '#c2410c' } }}
                        color={detailDialog.pii_disclosed ? 'inherit' : undefined}
                        disabled={piiDisclosedLoading}
                        onClick={handleTogglePiiDisclosed}
                        startIcon={<SecurityIcon />}
                      >
                        {detailDialog.pii_disclosed ? 'Unmark PII Disclosed' : 'Mark as PII Disclosed'}
                      </Button>
                    </Box>
                  </Box>
                  {detailDialog.pii_disclosed && detailDialog.pii_disclosed_marked_at && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Marked by {detailDialog.pii_disclosed_marked_by || 'unknown'} on{' '}
                      {format(new Date(detailDialog.pii_disclosed_marked_at), 'PPpp')}
                    </Typography>
                  )}
                </Paper>
              </Grid>

              <Grid item xs={12} sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" color="text.secondary">Content / Transcription</Typography>
                <Paper variant="outlined" sx={{ p: 2, mt: 1, bgcolor: '#f9f9f9', overflow: 'hidden' }}>
                  <Typography sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {detailDialog.content || detailDialog.transcription || '-'}
                  </Typography>
                </Paper>
              </Grid>
              {detailDialog.summary && (
                <Grid item xs={12} sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2" color="text.secondary">AI Summary / Response</Typography>
                  <Paper variant="outlined" sx={{ p: 2, mt: 1, bgcolor: '#f5f5ff', overflow: 'hidden' }}>
                    <Typography sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {detailDialog.summary}
                    </Typography>
                  </Paper>
                </Grid>
              )}
              {detailDialog.extracted_signals && Object.keys(detailDialog.extracted_signals).length > 0 && (
                <Grid item xs={12} sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2" color="text.secondary">Extracted Signals</Typography>
                  <Paper variant="outlined" sx={{ p: 2, mt: 1, bgcolor: '#fff8f0', overflow: 'hidden' }}>
                    {Array.isArray(detailDialog.extracted_signals) ? (
                      detailDialog.extracted_signals.map((signal, idx) => (
                        <Chip
                          key={idx}
                          label={typeof signal === 'string' ? signal : signal.type || signal.name || JSON.stringify(signal)}
                          size="small"
                          sx={{ mr: 0.5, mb: 0.5 }}
                          color={signal.level === 'HIGH' ? 'error' : signal.level === 'MODERATE' ? 'warning' : 'default'}
                        />
                      ))
                    ) : (
                      <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflow: 'hidden', fontSize: 12 }}>
                        {JSON.stringify(detailDialog.extracted_signals, null, 2)}
                      </pre>
                    )}
                  </Paper>
                </Grid>
              )}
              {detailDialog.metadata && Object.keys(detailDialog.metadata).length > 0 && (
                <Grid item xs={12} sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2" color="text.secondary">Metadata</Typography>
                  <Paper variant="outlined" sx={{ p: 2, mt: 1, bgcolor: '#f5f5f5', overflow: 'hidden' }}>
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflow: 'hidden', fontSize: 12 }}>
                      {JSON.stringify(detailDialog.metadata, null, 2)}
                    </pre>
                  </Paper>
                </Grid>
              )}
              {detailDialog.analysis && (
                <Grid item xs={12} sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2" color="text.secondary">Analysis</Typography>
                  <Paper variant="outlined" sx={{ p: 2, mt: 1, bgcolor: '#fff5f5', overflow: 'hidden' }}>
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflow: 'hidden', fontSize: 12 }}>
                      {typeof detailDialog.analysis === 'string'
                        ? detailDialog.analysis
                        : JSON.stringify(detailDialog.analysis, null, 2)}
                    </pre>
                  </Paper>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        {detailDialog && (
          <Box sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button onClick={() => setDetailDialog(null)}>Close</Button>
            <Button
              variant="contained"
              startIcon={<OpenIcon />}
              onClick={() => { setDetailDialog(null); navigate(`/users/${detailDialog.user_id}`); }}
            >
              View User
            </Button>
          </Box>
        )}
      </Dialog>
    </Box>
  );
};

export default Activity;
